import mapboxgl from 'mapbox-gl';
import React, { useEffect } from 'react';

import { toHash } from '@mono/to-hash';
import { LatLonEditor } from '@mono/lat-lon-editor/src/lat-lon-editor';

import { useObs } from './use-obs';
import { useSitesSelector, SitesList } from './use-sites-selector';
import { useGriddedDatasetSelector } from './use-gridded-dataset-selector';
import { GriddedDatasetSelector } from './gridded-dataset-select';
import { ObsSelect } from './obs-select';
import { SpaceConfiguratorProvider, useSpaceConfiguratorContext } from './space-configurator-context';
import { SpaceConfiguratorProps } from './types';
import { hoverLayer as sitesHoverLayer } from './sites-mapbox-config';
import { hoverLayer as obsHoverLayer } from './obs-mapbox-config';

export const SpaceConfigurator = (props: SpaceConfiguratorProps) => {
    return (
        <SpaceConfiguratorProvider {...props}>
            <SpaceConfiguratorComponent />
        </SpaceConfiguratorProvider>
    );
};

export const SpaceConfiguratorComponent = () => {
    const { enableSpace, enableSite, enableGriddedDataset, enableObs, state, setState, map, mapRef } = useSpaceConfiguratorContext();

    const { ObsSelectList, datasets: obsDatasets } = useObs();
    const { SitesSelectList } = useSitesSelector();
    const { griddedDatasets } = useGriddedDatasetSelector();

    // Plot the selected space marker
    useEffect(() => {
        if (!map) return;
        if (!state.space) return;

        const [lon, lat] = state.space.coordinates;
        const marker = new mapboxgl.Marker().setLngLat({ lat, lon }).addTo(map);

        if (!isNaN(lon) && !isNaN(lat)) {
            setTimeout(() => {
                if (map)
                    map.flyTo({
                        center: { lat, lon }, // The 0.8 makes it more centered given the floating UI in the top, obscuring some of the map
                        duration: 3e3,
                        zoom: Math.max(6, map.getZoom()),
                    });
            }, 100);
        }

        return () => {
            marker.remove();
        };
    }, [toHash(state.space), map]);

    // The onClick event handler for the map, this is for space selection
    useEffect(() => {
        if (!map) return;

        const onClick = (e: mapboxgl.MapMouseEvent) => {
            // If the user has allowed for selecting sites, then we need to check if the user has clicked on a site before we set the space
            const sitesLayer = map.getLayer(sitesHoverLayer.id);
            if (sitesLayer) {
                const siteFeatures = map.queryRenderedFeatures(e.point, { layers: [sitesHoverLayer.id] });
                if (siteFeatures.length > 0) return;
            }

            // If the user has allowed for selecting obs, then we need to check if the user has clicked on an obs before we set the space
            const obsLayer = map.getLayer(obsHoverLayer.id);
            if (obsLayer) {
                const obsFeatures = map.queryRenderedFeatures(e.point, { layers: [obsHoverLayer.id] });
                if (obsFeatures.length > 0) return;
            }

            if (!enableSpace) return;
            setState((prev) => {
                return {
                    ...prev,
                    space: { type: 'Point', coordinates: [Number(e.lngLat.lng.toFixed(6)), Number(e.lngLat.lat.toFixed(6))] },
                    movSiteId: undefined,
                };
            });
        };

        map.on('click', onClick);

        return () => {
            map.off('click', onClick);
        };
    }, [map, enableSite, enableSpace, enableObs]);

    return (
        <div style={{ display: 'flex', width: '100%', minHeight: 350, height: '100%' }}>
            <div style={{ width: '100%', position: 'relative' }}>
                <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
                {ObsSelectList}
                {SitesSelectList}
                <div
                    style={{
                        position: 'absolute',
                        top: 2,
                        left: 2,
                        paddingRight: 4,
                        zIndex: 10,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    {enableSpace && (
                        <LatLonEditor
                            space={state.space}
                            setSpace={(props) => {
                                setState((prev) => ({ ...prev, space: props, movSiteId: undefined }));
                            }}
                        />
                    )}
                    {enableSite && <SitesList />}
                    {enableGriddedDataset && <GriddedDatasetSelector datasetList={griddedDatasets} />}
                    {enableObs && <ObsSelect obsList={obsDatasets} />}
                </div>
            </div>
        </div>
    );
};
